import * as React from 'react';

import { cn } from '@/shared/lib/utils';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          // ── base ──────────────────────────────────────────────────────
          'tw-flex tw-h-10 tw-w-full tw-rounded-md tw-text-sm tw-text-foreground',
          // Explicit border + bg (preflight is OFF in this project, so the
          // browser/Bootstrap default border bleeds through unless we set
          // border-style + width + color explicitly. Bootstrap's .form-control
          // and the UA stylesheet both ship `border-style: inset`/`solid` with
          // their own colors — without `tw-border-solid` here the browser
          // default style leaks through and the border looks dark/3D.)
          'tw-border tw-border-solid tw-border-slate-200 tw-bg-white',
          'tw-px-3 tw-py-2 tw-shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
          'tw-transition-[border-color,box-shadow] tw-duration-150 tw-ease-out',
          // ── placeholder ───────────────────────────────────────────────
          'placeholder:tw-text-slate-400',
          // ── file input styling (drag-drop / file inputs) ──────────────
          'file:tw-border-0 file:tw-bg-transparent file:tw-text-sm file:tw-font-medium file:tw-text-foreground',
          // ── hover (subtle border darkening, no bg change) ─────────────
          'hover:tw-border-slate-300',
          // ── focus (soft sky glow, no harsh outline) ──────────────────
          'focus-visible:tw-outline-none focus-visible:tw-border-sky-400 focus-visible:tw-ring-4 focus-visible:tw-ring-sky-100',
          // ── disabled ──────────────────────────────────────────────────
          'disabled:tw-cursor-not-allowed disabled:tw-bg-slate-50 disabled:tw-text-slate-500 disabled:tw-opacity-100',
          // ── native date picker calendar icon (soft slate, subtle) ─────
          '[&::-webkit-calendar-picker-indicator]:tw-cursor-pointer [&::-webkit-calendar-picker-indicator]:tw-opacity-50 hover:[&::-webkit-calendar-picker-indicator]:tw-opacity-80',
          // ── dark mode ─────────────────────────────────────────────────
          'dark:tw-border-slate-800 dark:tw-bg-slate-950 dark:placeholder:tw-text-slate-500 dark:hover:tw-border-slate-700 dark:focus-visible:tw-border-sky-500 dark:focus-visible:tw-ring-sky-900/30',
          // ── kill Bootstrap .form-control overrides if class is applied ─
          // (Some legacy code still attaches .form-control; ensure our chrome wins.)
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input };
